import { useMemo, useState } from 'react';
import { Coins, Megaphone, Pencil, Plus, Radar, Search, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTableArea,
  CardTitle,
} from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { formatDate } from '@/lib/format';
import type { Campaign } from '@/types/campaigns';
import { CampaignDialog } from './CampaignDialog';
import { GastoDialog } from './GastoDialog';
import {
  alcanceLabel,
  campaignAlcance,
  countLabel,
  describeCampaignIds,
  describeUpdatedAt,
  filterCampaigns,
  formatNumber,
  hasAutoNombre,
  summarizeCampaigns,
  summarizeDescubrir,
  type CampaignAlcanceFiltro,
} from './campaigns-format';
import { activoPayload } from './campaigns-payload';
import { useCampaigns, useDeleteCampaign, useDiscoverCampaigns, useUpdateCampaign } from './use-campaigns';

/**
 * Pantalla `/campaigns` (paso 8 del frontend) — el gasto por anuncio/campaña del negocio activo.
 *
 * Qué es esta pantalla y qué NO es:
 *  - ES la única fuente de gasto del CPA mientras no exista el sync con la Meta Ads API: el admin
 *    carga a mano cuánto se gastó en el periodo, por anuncio (`ad_id`) o por campaña entera;
 *  - NO calcula el CPA: eso lo hace `/reports` con estos números. Acá no se muestra ninguna tasa de
 *    conversión para no tener dos versiones del mismo indicador.
 *
 * Filtros con el reparto explícito entre servidor y cliente: `activo` viaja al backend (es un query
 * param real y de él dependen `total` y `gastoTotal`), mientras el buscador y el alcance se resuelven
 * sobre las filas ya cargadas (el endpoint no pagina: `items` es el catálogo entero).
 *
 * Permisos: leer el gasto lo puede hacer cualquier miembro del negocio (los reportes usan el mismo
 * dato), pero cargarlo, editarlo, borrarlo y «descubrir» son solo de ADMIN. Se usan los mismos
 * controles que en Productos y CRM —`canEdit` de `useActiveTenantAccess` esconde los botones y el
 * switch— y el backend responde 403 igual si alguien invoca los endpoints a mano. `<RequireRole>`
 * queda para las pantallas enteras solo-ADMIN (Equipo), que no es el caso.
 */

/** Filtro de estado de la tabla: los tres valores de la pantalla (el backend solo acepta dos). */
type ActivoFiltro = 'todos' | 'true' | 'false';

const ACTIVO_FILTERS: { value: ActivoFiltro; label: string }[] = [
  { value: 'todos', label: 'Activas e inactivas' },
  { value: 'true', label: 'Solo las que cuentan' },
  { value: 'false', label: 'Solo las que no cuentan' },
];

const ALCANCE_FILTERS: { value: CampaignAlcanceFiltro; label: string }[] = [
  { value: 'todos', label: 'Todo el gasto' },
  { value: 'anuncio', label: 'Gasto por anuncio' },
  { value: 'campana', label: 'Gasto por campaña' },
];

export function CampaignsPage() {
  const { tenantId, canEdit } = useActiveTenantAccess();

  const updateCampaign = useUpdateCampaign(tenantId);
  const deleteCampaign = useDeleteCampaign(tenantId);
  const discoverCampaigns = useDiscoverCampaigns(tenantId);

  const [search, setSearch] = useState('');
  const [alcanceFilter, setAlcanceFilter] = useState<CampaignAlcanceFiltro>('todos');
  const [activoFilter, setActivoFilter] = useState<ActivoFiltro>('todos');

  /** Los filtros que viajan al backend: solo `activo` (la búsqueda y el alcance son de cliente). */
  const campaignsQuery = useCampaigns(tenantId, {
    activo: activoFilter === 'todos' ? undefined : activoFilter,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  /** Fila a la que se le está actualizando el gasto (`null` = diálogo cerrado). */
  const [gastoCampaign, setGastoCampaign] = useState<Campaign | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Campaign | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Lo que respondió el último «Descubrir anuncios» (se queda a la vista hasta que se cierre). */
  const [discoverMessage, setDiscoverMessage] = useState<string | null>(null);

  const result = campaignsQuery.data;
  const campaigns = result?.items ?? [];

  const visibleCampaigns = useMemo(
    () => filterCampaigns(campaigns, { search, alcance: alcanceFilter }),
    [campaigns, search, alcanceFilter],
  );

  const filtering =
    search.trim().length > 0 || alcanceFilter !== 'todos' || activoFilter !== 'todos';
  /** Hay filas cargadas, pero nada pasa los filtros: se distingue de «todavía no hay nada». */
  const noMatches = campaigns.length > 0 && visibleCampaigns.length === 0;
  /** El filtro de estado dejó la tabla vacía: no es «no hay nada cargado», es «nada cumple». */
  const emptyByActivo = result !== undefined && result.total === 0 && activoFilter !== 'todos';
  /** Negocio sin ninguna fila de gasto (ni con filtro ni sin él). */
  const nothingYet = result !== undefined && result.total === 0 && activoFilter === 'todos';

  function openCreate() {
    setEditing(null);
    setActionError(null);
    setDialogOpen(true);
  }

  function openEdit(campaign: Campaign) {
    setEditing(campaign);
    setActionError(null);
    setDialogOpen(true);
  }

  function clearFilters() {
    setSearch('');
    setAlcanceFilter('todos');
    setActivoFilter('todos');
  }

  /**
   * Activar/desactivar sin abrir el modal: un `PATCH` con un solo campo (`activo`). Es la forma de
   * sacar del CPA un anuncio que ya terminó sin perder su gasto cargado.
   */
  async function toggleActive(campaign: Campaign) {
    setActionError(null);
    try {
      await updateCampaign.mutateAsync({
        campaignId: campaign.id,
        payload: activoPayload(!campaign.activo),
      });
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    setActionError(null);
    try {
      await deleteCampaign.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  /**
   * `POST .../campaigns/descubrir`: crea con gasto 0 las filas de los `ad_id`/`campaign_id` que ya
   * aparecieron en las conversaciones. Es idempotente, así que se puede repetir sin miedo y no hace
   * falta confirmar: el resultado se cuenta arriba, en un aviso de éxito.
   */
  /**
   * `POST .../campaigns/descubrir`: crea con gasto 0 las filas de los `ad_id`/`campaign_id` que ya
   * aparecieron en las conversaciones. Es idempotente, así que se puede repetir sin miedo y no hace
   * falta confirmar: el resultado se cuenta arriba, en un aviso de éxito.
   */
  async function runDiscover() {
    setActionError(null);
    setDiscoverMessage(null);
    try {
      const discovered = await discoverCampaigns.mutateAsync();
      setDiscoverMessage(summarizeDescubrir(discovered));
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            canEdit ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Radar size={14} />}
                  loading={discoverCampaigns.isPending}
                  onClick={() => void runDiscover()}
                >
                  Descubrir anuncios
                </Button>
                <Button size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
                  Nueva fila de gasto
                </Button>
              </>
            ) : null
          }
        >
          <CardTitle>Gasto de anuncios y campañas</CardTitle>
          <CardDescription>
            {result ? `${summarizeCampaigns(result)}. ` : ''}Una fila = una unidad de gasto: el
            anuncio concreto (el `ad_id` que llega en el referral) o la campaña entera cuando no se
            puede desglosar. Este número es el que usan los reportes para el CPA de cada campaña: el
            CPA se calcula en Reportes, acá solo se carga.{' '}
            {canEdit
              ? '«Descubrir anuncios» crea con gasto 0 las filas de los anuncios que ya aparecieron en las conversaciones, así no hay que copiar ids a mano.'
              : 'Tu rol en este negocio es de solo lectura: solo un administrador puede cargar, editar o borrar el gasto.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {actionError ? <Alert>{actionError}</Alert> : null}

          {discoverMessage ? (
            <Alert tone="success">
              <p className="font-medium">{discoverMessage}</p>
              <p className="mt-1">
                Las filas nuevas llegan con gasto 0 y un nombre provisorio: ábreles el gasto y
                renómbralas, o el CPA del reporte va a salir más bajo de lo real.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 -ml-2"
                onClick={() => setDiscoverMessage(null)}
              >
                Entendido
              </Button>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <Input
              type="search"
              value={search}
              leftIcon={<Search size={16} />}
              placeholder="Buscar por nombre, id de anuncio o id de campaña…"
              aria-label="Buscar filas de gasto"
              onChange={(event) => setSearch(event.target.value)}
            />

            <Select
              aria-label="Filtrar por estado"
              className="lg:w-56"
              value={activoFilter}
              onChange={(event) => setActivoFilter(event.target.value as ActivoFiltro)}
            >
              {ACTIVO_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por alcance"
              className="lg:w-56"
              value={alcanceFilter}
              onChange={(event) =>
                setAlcanceFilter(event.target.value as CampaignAlcanceFiltro)
              }
            >
              {ALCANCE_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          {campaignsQuery.isPending ? <PageLoader label="Cargando el gasto…" /> : null}

          {campaignsQuery.isError ? <Alert>{getErrorMessage(campaignsQuery.error)}</Alert> : null}

          {campaignsQuery.isSuccess ? (
            <>
              {nothingYet ? (
                <EmptyState
                  icon={<Megaphone size={22} />}
                  title="Todavía no hay gasto cargado"
                  description="Sin estas filas los reportes no pueden calcular el CPA. Empieza por «Descubrir anuncios»: busca los ad_id y campaign_id que ya llegaron en las conversaciones y crea las filas por ti (con gasto 0). Si prefieres, carga el gasto a mano."
                  action={
                    canEdit ? (
                      <span className="flex flex-wrap items-center justify-center gap-2">
                        <Button
                          variant="secondary"
                          leftIcon={<Radar size={16} />}
                          loading={discoverCampaigns.isPending}
                          onClick={() => void runDiscover()}
                        >
                          Buscar los anuncios que ya aparecieron
                        </Button>
                        <Button leftIcon={<Plus size={16} />} onClick={openCreate}>
                          Cargar el gasto a mano
                        </Button>
                      </span>
                    ) : undefined
                  }
                />
              ) : null}

              {emptyByActivo ? (
                <EmptyState
                  icon={<Search size={22} />}
                  title="No hay filas con ese estado"
                  description="Ninguna fila de gasto cumple el filtro de estado que elegiste. Las que «no cuentan» quedan fuera del CPA del reporte, pero siguen guardadas."
                  action={
                    <Button variant="secondary" onClick={clearFilters}>
                      Quitar los filtros
                    </Button>
                  }
                />
              ) : null}

              {noMatches ? (
                <EmptyState
                  icon={<Search size={22} />}
                  title="Ninguna fila coincide"
                  description={
                    search.trim().length > 0
                      ? `Nada en el gasto coincide con «${search.trim()}»${
                          alcanceFilter === 'todos'
                            ? ''
                            : alcanceFilter === 'anuncio'
                              ? ' entre las filas por anuncio'
                              : ' entre las filas por campaña'
                        }.`
                      : 'No hay filas de ese alcance: el filtro es «por anuncio» o «por campaña».'
                  }
                  action={
                    <Button variant="secondary" onClick={clearFilters}>
                      Quitar los filtros
                    </Button>
                  }
                />
              ) : null}

              {filtering && !noMatches ? (
                <p className="text-caption text-ink-muted">
                  {countLabel(visibleCampaigns.length, 'fila visible', 'filas visibles')} de{' '}
                  {formatNumber(result?.total ?? 0)}.
                </p>
              ) : null}

              {visibleCampaigns.length > 0 ? (
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Fila de gasto</TableHeaderCell>
                        <TableHeaderCell>Alcance</TableHeaderCell>
                        <TableHeaderCell align="right">Gasto acumulado</TableHeaderCell>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell align="right">Acciones</TableHeaderCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleCampaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell>
                            <span className="flex max-w-md flex-col gap-0.5">
                              <span className="text-body font-medium text-ink">
                                {campaign.nombre}
                              </span>

                              <span className="line-clamp-2 text-caption text-ink-muted">
                                {describeCampaignIds(campaign)}
                              </span>

                              <span className="mt-0.5 text-micro text-ink-faint">
                                {describeUpdatedAt(formatDate(campaign.updatedAt))}
                              </span>

                              {hasAutoNombre(campaign) ? (
                                <Badge tone="warning" className="mt-1 w-fit">
                                  Nombre provisorio: renómbrala
                                </Badge>
                              ) : null}
                            </span>
                          </TableCell>

                          <TableCell>
                            <Badge tone={campaignAlcance(campaign) === 'anuncio' ? 'brand' : 'info'}>
                              {alcanceLabel(campaign)}
                            </Badge>
                          </TableCell>

                          <TableCell align="right">
                            <span className="flex flex-col items-end gap-0.5">
                              <span className="tabular text-body font-medium text-ink">
                                {formatNumber(campaign.gastoActual)}
                              </span>
                              <span className="text-micro text-ink-faint">
                                {campaign.moneda} acumulado
                              </span>
                            </span>
                          </TableCell>

                          <TableCell>
                            <span className="flex items-center gap-2.5">
                              <Switch
                                checked={campaign.activo}
                                label={`Contar el gasto de ${campaign.nombre} en los reportes`}
                                disabled={!canEdit || updateCampaign.isPending}
                                onChange={() => void toggleActive(campaign)}
                              />
                              <Badge tone={campaign.activo ? 'success' : 'neutral'}>
                                {campaign.activo ? 'Cuenta' : 'No cuenta'}
                              </Badge>
                            </span>
                          </TableCell>

                          <TableActionsCell>
                            <span className="inline-flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actualizar el gasto de ${campaign.nombre}`}
                                disabled={!canEdit}
                                onClick={() => setGastoCampaign(campaign)}
                              >
                                <Coins size={16} />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Editar ${campaign.nombre}`}
                                disabled={!canEdit}
                                onClick={() => openEdit(campaign)}
                              >
                                <Pencil size={16} />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Eliminar ${campaign.nombre}`}
                                disabled={!canEdit}
                                onClick={() => setPendingDelete(campaign)}
                              >
                                <Trash size={16} />
                              </Button>
                            </span>
                          </TableActionsCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardTableArea>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <CampaignDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tenantId={tenantId}
        campaign={editing}
      />

      <GastoDialog
        open={gastoCampaign !== null}
        onClose={() => setGastoCampaign(null)}
        tenantId={tenantId}
        campaign={gastoCampaign}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Eliminar la fila de gasto"
        description={
          pendingDelete
            ? `Se borra «${pendingDelete.nombre}» (${describeCampaignIds(pendingDelete)}) con su gasto acumulado de ${formatNumber(pendingDelete.gastoActual)} ${pendingDelete.moneda}. Si el anuncio solo terminó, desactívala en vez de borrarla: deja de contar para el CPA y conserva el gasto cargado.`
            : undefined
        }
        confirmLabel="Eliminar la fila"
        loading={deleteCampaign.isPending}
        error={actionError}
      />
    </>
  );
}
